import React, { useState } from 'react';
import { Camera, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { OwnerButton, OwnerCard, OwnerShell } from '../OwnerShell';
import { SelectField, TextField } from '../OwnerFields';
import { UploadTile } from '../UploadTile';
import type { UploadState } from '../UploadTile';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { ownerProfile } from '../../../data/ownerProfile';
import { profileTone } from '../../../utils/ownerStatus';
import type { OwnerNav } from '../../../types/owner';
export function OwnerProfileForm({
  nav
}: {
  nav: OwnerNav;
}) {
  const {
    myProfileStatus,
    submitProfile,
    registrations,
    ownerId
  } = useOwnerService();
  const remark = registrations.find(r => r.id === ownerId)?.remark;
  const p = ownerProfile;
  const [f, setF] = useState({
    name: p.name,
    father: p.father,
    mother: p.mother,
    nid: p.nid,
    dob: p.dob,
    gender: p.gender,
    mobile: p.mobile,
    alt: p.altMobile,
    email: p.email,
    present: p.present,
    permanent: p.permanent,
    division: p.division,
    district: p.district,
    thana: p.thana,
    post: p.post
  });
  const [photo, setPhoto] = useState(true);
  const [docs, setDocs] = useState<Record<string, UploadState>>({
    front: 'uploaded',
    back: 'missing',
    selfie: 'uploaded'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof f) => (v: string) => setF(s => ({
    ...s,
    [k]: v
  }));
  const tone = profileTone(myProfileStatus);
  const StatusIcon = tone === 'green' ? CheckCircle2 : tone === 'orange' && myProfileStatus === 'সংশোধন প্রয়োজন' ? AlertTriangle : Clock;
  const submit = () => {
    const e: Record<string, string> = {};
    if (!f.father.trim()) e.father = 'পিতার নাম আবশ্যক';
    if (!f.mother.trim()) e.mother = 'মাতার নাম আবশ্যক';
    if (!f.present.trim()) e.present = 'বর্তমান ঠিকানা আবশ্যক';
    if (docs.back !== 'uploaded') e.back = 'NID পেছনের অংশ আপলোড করুন';
    setErrors(e);
    if (Object.keys(e).length) return nav.showToast('কিছু আবশ্যক তথ্য বাকি রয়েছে');
    submitProfile();
    nav.showToast('প্রোফাইল তথ্য জমা হয়েছে — ডিএমপি যাচাই অপেক্ষমাণ');
    nav.reset('home');
  };
  const statusCls = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    orange: 'border-amber-200 bg-amber-50 text-amber-800',
    red: 'border-red-200 bg-red-50 text-red-800',
    blue: '',
    gray: ''
  }[tone];
  return <OwnerShell nav={nav} title="মালিকের ব্যক্তিগত তথ্য" subtitle={`প্রোফাইল আইডি: ${ownerId}`} footer={<div className="grid grid-cols-2 gap-2" data-id="element-1852" data-mp="1gysivi1hg7zc">
          <OwnerButton variant="outline" onClick={() => nav.showToast('খসড়া সংরক্ষণ করা হয়েছে')} data-id="element-1853" data-mp="1gvwlpvxglnby">খসড়া সংরক্ষণ</OwnerButton>
          <OwnerButton variant="green" onClick={submit} data-id="element-1854" data-mp="17xklr3apdd6u">তথ্য জমা দিন</OwnerButton>
        </div>} data-id="element-1851" data-mp="1lwu4oaqamlei">
      <div className={`mb-4 flex items-start gap-2.5 rounded-xl border p-3 text-[13px] ${statusCls}`} data-id="element-1855" data-mp="1hieqt4nw8ij">
        <StatusIcon className="mt-0.5 h-5 w-5 shrink-0" data-id="element-1856" data-mp="3sn0j6h4fg05g" />
        <div data-id="element-1857" data-mp="3spmrwk320lp9">
          <p className="font-semibold" data-id="element-1858" data-mp="2857rl7ung05c">প্রোফাইল অবস্থা: {myProfileStatus}</p>
          <p className="text-[11.5px] opacity-90" data-id="element-1859" data-mp="2834wwaijxiua">{myProfileStatus === 'সংশোধন প্রয়োজন' ? remark ?? 'নির্দেশনা অনুযায়ী তথ্য সংশোধন করুন।' : myProfileStatus === 'যাচাইকৃত' ? 'NID তথ্য ডিএমপি কর্তৃক যাচাইকৃত।' : 'ডিএমপি কর্মকর্তা আপনার তথ্য যাচাই করছেন।'}</p>
        </div>
      </div>

      <div className="mb-4 flex flex-col items-center" data-id="element-1860" data-mp="2lxxhvoh7nr89">
        <button onClick={() => {
        setPhoto(true);
        nav.showToast('ছবি হালনাগাদ হয়েছে');
      }} className="relative" aria-label="মালিকের ছবি পরিবর্তন" data-id="element-1861" data-mp="2qw8uu82syhup">
          {photo ? <img src={p.photo} alt="মালিকের ছবি" className="h-24 w-24 rounded-full border-4 border-white object-cover shadow" data-id="element-1862" data-mp="2vwws3bhmjuxd" /> : <span className="flex h-24 w-24 rounded-full bg-slate-200" data-id="element-1863" data-mp="34vi5ks88ojra" />}
          <span className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-navy-800 text-white shadow" data-id="element-1864" data-mp="34up68cbm0wj6"><Camera className="h-4 w-4" data-id="element-1865" data-mp="1ahhc6mvgkoyl" /></span>
        </button>
        <p className="mt-1.5 text-xs text-slate-500" data-id="element-1866" data-mp="1ahr1e88ois38">মালিকের ছবি</p>
      </div>

      <OwnerCard title="ব্যক্তিগত তথ্য" data-id="element-1867" data-mp="f5zm05t9gyz9">
        <div className="space-y-3" data-id="element-1868" data-mp="f95d1qlgaaxm">
          <TextField id="p-name" label="পূর্ণ নাম" required value={f.name} onChange={set('name')} data-id="element-1869" data-mp="b6h1vc99krxh" />
          <TextField id="p-father" label="পিতার নাম" required value={f.father} onChange={set('father')} error={errors.father} data-id="element-1870" data-mp="p5qgw8lqhfzv" />
          <TextField id="p-mother" label="মাতার নাম" required value={f.mother} onChange={set('mother')} error={errors.mother} data-id="element-1871" data-mp="k6x30f2jbmz6" />
          <TextField id="p-nid" label="জাতীয় পরিচয়পত্র নম্বর" required value={f.nid} readOnly hint="নিবন্ধনের সময় দেওয়া — পরিবর্তনযোগ্য নয়" data-id="element-1872" data-mp="3sjul74h3w7r9" />
          <div className="grid grid-cols-2 gap-3" data-id="element-1873" data-mp="66w598hkgvnx">
            <TextField id="p-dob" label="জন্মতারিখ" required value={f.dob} onChange={set('dob')} data-id="element-1874" data-mp="68fjktjnwwn2" />
            <SelectField id="p-gender" label="লিঙ্গ" value={f.gender} options={['পুরুষ', 'মহিলা', 'তৃতীয় লিঙ্গ']} onChange={set('gender')} data-id="element-1875" data-mp="1e2xscupr985" />
          </div>
        </div>
      </OwnerCard>

      <OwnerCard title="যোগাযোগ" className="mt-3" data-id="element-1876" data-mp="34uieqcd5ydsn">
        <div className="space-y-3" data-id="element-1877" data-mp="1kebvyrqcz4mh">
          <TextField id="p-mob" label="মোবাইল নম্বর" value={f.mobile} readOnly hint="✓ OTP দ্বারা যাচাইকৃত" data-id="element-1878" data-mp="1kd970pcljc97" />
          <TextField id="p-alt" label="বিকল্প মোবাইল" value={f.alt} onChange={set('alt')} data-id="element-1879" data-mp="1p7deratorobc" />
          <TextField id="p-mail" label="ইমেইল (ঐচ্ছিক)" value={f.email} onChange={set('email')} data-id="element-1880" data-mp="1bfqi39vu0ds1" />
        </div>
      </OwnerCard>

      <OwnerCard title="ঠিকানা" className="mt-3" data-id="element-1881" data-mp="2351yugfxirfc">
        <div className="space-y-3" data-id="element-1882" data-mp="236d1gcxn1t4v">
          <TextField id="p-pre" label="বর্তমান ঠিকানা" required value={f.present} onChange={set('present')} error={errors.present} data-id="element-1883" data-mp="2338ux8wpalb1" />
          <TextField id="p-per" label="স্থায়ী ঠিকানা" value={f.permanent} onChange={set('permanent')} data-id="element-1884" data-mp="1u56kk2r2ugb3" />
          <div className="grid grid-cols-2 gap-3" data-id="element-1885" data-mp="1z2ovfyonn5lq">
            <SelectField id="p-div" label="বিভাগ" value={f.division} options={['ঢাকা', 'চট্টগ্রাম', 'রাজশাহী', 'খুলনা', 'বরিশাল', 'সিলেট', 'রংপুর', 'ময়মনসিংহ']} onChange={set('division')} data-id="element-1886" data-mp="1z5kr1gd15i70" />
            <SelectField id="p-dis" label="জেলা" value={f.district} options={['ঢাকা', 'গাজীপুর', 'নারায়ণগঞ্জ', 'টাঙ্গাইল']} onChange={set('district')} data-id="element-1887" data-mp="nm8bpt62i2t2" />
            <SelectField id="p-th" label="উপজেলা/থানা" value={f.thana} options={['মিরপুর মডেল থানা', 'পল্লবী থানা', 'কাফরুল থানা', 'শাহ আলী থানা']} onChange={set('thana')} data-id="element-1888" data-mp="2ke4agk2otsvz" />
            <TextField id="p-post" label="পোস্ট কোড" value={f.post} onChange={set('post')} data-id="element-1889" data-mp="2bmc34i8ymzvm" />
          </div>
        </div>
      </OwnerCard>

      <h2 className="mb-2 mt-4 text-sm font-semibold text-navy-900" data-id="element-1890" data-mp="26nsgefmjlk27">ডকুমেন্ট আপলোড</h2>
      <div className="space-y-2.5" data-id="element-1891" data-mp="21nmsnaje8hpe">
        {[['front', 'NID সামনের অংশ'], ['back', 'NID পেছনের অংশ'], ['selfie', 'মালিকের ছবি']].map(([k, l]) => <UploadTile key={k} label={l} required state={docs[k]} fileName={`${k}_nid_2026.jpg`} preview={k === 'selfie' ? p.photo : undefined} onUpload={() => setDocs(d => ({
        ...d,
        [k]: 'uploaded'
      }))} data-id="element-1892" data-mp="1snzsst6bbjyg" />)}
        {errors.back && <p className="text-[11px] font-medium text-red-600" data-id="element-1893" data-mp="3n1qxrfdp5fnk">{errors.back}</p>}
      </div>
    </OwnerShell>;
}